import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from './input.jsx';
import { cn } from '../lib/utils.js';

/**
 * Search field with debounce. `value` is the committed (e.g. URL) value; `onValueChange`
 * fires `delay` ms after the user stops typing. External changes (back button) are adopted.
 * @param {Omit<import('react').ComponentProps<'input'>, 'value'|'onChange'> & {
 *   value?: string,
 *   onValueChange: (value: string) => void,
 *   delay?: number,
 *   label?: string,
 *   wrapperClassName?: string,
 * }} props
 */
export function SearchInput({ value = '', onValueChange, delay = 300, label = 'Suchen', className, wrapperClassName, ...props }) {
  const [text, setText] = useState(value);
  const [committed, setCommitted] = useState(value);
  const [emitted, setEmitted] = useState(value);

  // Adopt external changes (e.g. browser navigation), but not the echo of our own emit.
  if (value !== committed) {
    setCommitted(value);
    if (value !== emitted) {
      setText(value);
      setEmitted(value);
    }
  }

  useEffect(() => {
    if (text === emitted) return undefined;
    const timer = setTimeout(() => {
      setEmitted(text);
      onValueChange(text.trim());
    }, delay);
    return () => clearTimeout(timer);
  }, [text, emitted, delay, onValueChange]);

  return (
    <div data-slot="search-input" className={cn('relative w-full sm:max-w-xs', wrapperClassName)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input type="search" aria-label={label} value={text} onChange={(e) => setText(e.target.value)} className={cn('pl-8', className)} {...props} />
    </div>
  );
}
